/**
 * RadioCard — the standard radio item card.
 *
 * Three rows:
 *   1. Available  (clickable → marks available, clears name)
 *   2. Assigned   (display-only — shows last name or "Unassigned")
 *   3. Not Avail. (clickable → prompts for last holder, marks unavailable)
 *
 * Plus an OOS state and a +30%/grayscale image when neither available nor
 * actively assigned.
 *
 * This is one of THREE radio card variants:
 *   - RadioCard          (this file) — standard radios
 *   - DutyRadioCard      — radios 95/97/99, auto on-duty from personnel
 *   - StaticRadioCard    — radios 105/108/113/118, permanent label
 *
 * The parent RadioSection picks which to render per radio.id.
 */
import { Card, StatusDot, dotLabelColor } from '../ui'
import { WALKIE_ICON } from '../../assets'
import type { RadioSlot } from '../../stores/desktop'

interface RadioCardProps {
  radio: RadioSlot
  /** Friendly noun for the prompt: "Sup Radio", "Off Radio", "Other Radio". */
  naType: string
  onUpdate: (patch: Partial<RadioSlot>) => void
}

/** Trim "(nickname)" then take the last word, mirroring FOM's Re() helper. */
function shortName(name: string): string {
  const s = name.replace(/\s*\(.*?\)\s*/g, '').trim()
  return s.includes(',') ? s.split(',')[0].trim() : (s.split(' ').pop() ?? s)
}

export default function RadioCard({ radio, naType, onUpdate }: RadioCardProps) {
  const isOos      = !!radio.oos
  const isAssigned = !isOos && !radio.available && !!radio.assignedTo
  const isAvail    = !isOos && radio.available
  const isNotAvail = !isOos && !radio.available && !radio.assignedTo

  const borderColor = isOos      ? '#fca5a5'
                    : isAssigned ? '#3b82f6'
                    : isNotAvail ? '#f87171'
                    : isAvail    ? '#4ade80'
                                 : '#e5e7eb'

  const imgActive = isAssigned || isAvail

  function handleAvailable() {
    onUpdate({ available: true, assignedTo: '' })
  }

  function handleNotAvail() {
    let lastHolder = radio.assignedTo || ''
    if (!lastHolder) {
      lastHolder = window.prompt(`${naType} ${radio.id} — Who had it last?`) || 'Unknown'
    }
    onUpdate({ available: false, assignedTo: lastHolder })
  }

  return (
    <Card variant="item" borderColor={borderColor}>
      <div
        style={{
          fontSize: 13,
          fontWeight: 700,
          color: 'var(--text-muted)',
          marginBottom: 4,
          textAlign: 'center',
        }}
      >
        Radio {radio.id}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <div style={{ flexShrink: 0, width: 60, height: 60 }}>
          <img
            src={WALKIE_ICON}
            alt={`Radio ${radio.id}`}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              opacity: imgActive ? 1 : 0.4,
              filter:  imgActive ? 'none' : 'grayscale(100%)',
            }}
          />
        </div>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 4,
            flex: 1,
            minWidth: 0,
          }}
        >
          {isOos ? (
            <span style={{ fontSize: 11, fontWeight: 700, color: '#dc2626' }}>OOS</span>
          ) : (
            <>
              {/* Row 1 — Available */}
              <div
                onClick={handleAvailable}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  cursor: 'pointer',
                  opacity: isAvail ? 1 : 0.3,
                }}
              >
                <StatusDot variant="green" active={isAvail} />
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: dotLabelColor('green', isAvail),
                  }}
                >
                  Available
                </span>
              </div>

              {/* Row 2 — Assigned (display only) */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  opacity: isAssigned ? 1 : 0.3,
                }}
              >
                <StatusDot variant="blue" active={isAssigned} />
                {isAssigned ? (
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: dotLabelColor('blue', true),
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {shortName(radio.assignedTo)}
                  </span>
                ) : (
                  <span style={{ fontSize: 11, color: '#9ca3af' }}>Unassigned</span>
                )}
              </div>

              {/* Row 3 — Not Available */}
              <div
                onClick={handleNotAvail}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  cursor: 'pointer',
                  opacity: isNotAvail ? 1 : 0.3,
                }}
              >
                <StatusDot variant="red" active={isNotAvail} />
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: dotLabelColor('red', isNotAvail),
                  }}
                >
                  Not Avail.
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    </Card>
  )
}
